import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Statistic,
  Table,
  Tag,
  Space,
  message,
  Popconfirm,
  Tooltip,
  Spin
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  UploadOutlined,
  PictureOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../../components/Layout';
import { useUser } from '../../contexts/userContext';
import { resellerService } from '../../services/reseller.service';
import { ResellerDetail, CouponSet } from '../../types/reseller.types';
import CouponSetDetailModal from '../../components/Admin/CouponSetDetailModal';
import RedemptionsTable from '../../components/Admin/RedemptionsTable';
import MonthlyRedemptionsCsvExport from '../../components/Admin/MonthlyRedemptionsCsvExport';
import '../../Styles/Resellers.scss';

const ResellerDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { state: { isAdmin } } = useUser();

  const [resellerDetail, setResellerDetail] = useState<ResellerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCouponSetId, setSelectedCouponSetId] = useState<number | null>(null);
  const [isCouponSetModalVisible, setIsCouponSetModalVisible] = useState(false);

  useEffect(() => {
    if (isAdmin === false) {
      navigate('/');
      return;
    }
    if (isAdmin && id) {
      loadData();
    }
  }, [isAdmin, id, navigate]);

  const loadData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await resellerService.getResellerDetail(parseInt(id));

      if (response.isSuccess && response.responseObject) {
        setResellerDetail(response.responseObject);
      } else {
        message.error('Failed to load reseller data');
      }
    } catch (error) {
      message.error('Failed to load reseller data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const response = await resellerService.deleteReseller(parseInt(id));
      if (response.isSuccess) {
        message.success('Reseller deactivated successfully');
        navigate('/admin/resellers');
      } else {
        message.error('Failed to delete reseller');
      }
    } catch (error) {
      message.error('Failed to delete reseller');
    }
  };

  const copyActivationUrl = () => {
    if (!resellerDetail) return;
    const url = `${window.location.origin}/activate?r=${encodeURIComponent(resellerDetail.name)}`;
    navigator.clipboard.writeText(url);
    message.success('Activation URL copied to clipboard');
  };

  const handleCouponSetClick = (couponSetId: number) => {
    setSelectedCouponSetId(couponSetId);
    setIsCouponSetModalVisible(true);
  };

  const handleCloseCouponSetModal = () => {
    setIsCouponSetModalVisible(false);
    setSelectedCouponSetId(null);
  };

  const couponSetColumns: ColumnsType<CouponSet> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <a onClick={() => handleCouponSetClick(record.id)} className="coupon-set-link">
          {name}
        </a>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Price',
      dataIndex: 'pricePerLicense',
      key: 'pricePerLicense',
      render: (price) => `$${price.toFixed(2)}`
    },
    {
      title: 'Total',
      dataIndex: 'totalCodes',
      key: 'totalCodes'
    },
    {
      title: 'Redeemed',
      dataIndex: 'redeemedCodes',
      key: 'redeemedCodes',
      render: (count) => count || 0
    },
    {
      title: 'Available',
      dataIndex: 'availableCodes',
      key: 'availableCodes',
      render: (count) => count || 0
    }
  ];

  if (isAdmin === null || loading) {
    return (
      <Layout>
        <div className="reseller-detail-page">
          <div className="page-loading">
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              tip="Loading reseller data..."
            />
          </div>
        </div>
      </Layout>
    );
  }

  if (!resellerDetail) {
    return (
      <Layout>
        <div className="reseller-detail-page">
          <div className="reseller-detail-page__not-found">
            <h2>Reseller not found</h2>
            <Button onClick={() => navigate('/admin/resellers')}>
              Back to Resellers
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const activationUrl = `${window.location.origin}/activate?r=${encodeURIComponent(resellerDetail.name)}`;

  return (
    <Layout>
      <div className="reseller-detail-page">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/resellers')}
          className="back-button"
        >
          Back
        </Button>

        <div className="reseller-detail-page__header">
          <div className="reseller-detail-page__title">
            {resellerDetail.logoUrl ? (
              <img
                src={resellerDetail.logoUrl}
                alt={resellerDetail.name}
                className="reseller-logo-large"
              />
            ) : (
              <div className="reseller-logo-placeholder-large">
                <PictureOutlined />
              </div>
            )}
            <div>
              <h1>{resellerDetail.name}</h1>
              <p className="reseller-meta">
                {resellerDetail.code && (
                  <>
                    Code: <code>{resellerDetail.code}</code>
                    {resellerDetail.contactEmail && <span> | {resellerDetail.contactEmail}</span>}
                    <span> | </span>
                  </>
                )}
                {!resellerDetail.code && resellerDetail.contactEmail && (
                  <>
                    {resellerDetail.contactEmail}
                    <span> | </span>
                  </>
                )}
                <Tag color={resellerDetail.isActive ? 'green' : 'default'}>
                  {resellerDetail.isActive ? 'Active' : 'Inactive'}
                </Tag>
              </p>
            </div>
          </div>
          <Space>
            <MonthlyRedemptionsCsvExport 
              resellerId={parseInt(id!)} 
              resellerCode={resellerDetail.code || undefined}
            />
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/resellers/${id}/edit`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this reseller?"
              onConfirm={handleDelete}
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </div>

        {resellerDetail.stats && (
          <div className="reseller-stats">
            <Card className="stat-card">
              <Statistic
                title="Total Revenue"
                value={resellerDetail.stats.totalRevenue}
                prefix="$"
                precision={2}
              />
            </Card>
            <Card className="stat-card">
              <Statistic title="Total Codes" value={resellerDetail.stats.totalCodes} />
            </Card>
            <Card className="stat-card">
              <Statistic title="Redeemed" value={resellerDetail.stats.redeemedCodes} />
            </Card>
            <Card className="stat-card">
              <Statistic title="Available" value={resellerDetail.stats.availableCodes} />
            </Card>
          </div>
        )}

        <Card className="activation-url-card">
          <h3>Activation URL</h3>
          <div className="activation-url">
            <code>{activationUrl}</code>
            <Tooltip title="Copy URL">
              <Button icon={<CopyOutlined />} onClick={copyActivationUrl} />
            </Tooltip>
          </div>
          {resellerDetail.code && (
            <p className="activation-url-example">
              Full Code Example: <code>{resellerDetail.code.toUpperCase()}XXXXXX</code>
            </p>
          )}
        </Card>

        <Card
          title={`Coupon Sets (${resellerDetail.couponSets?.length || 0})`}
          extra={
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => navigate(`/admin/import?resellerId=${id}`)}
            >
              Import CSV
            </Button>
          }
          className="coupon-sets-card"
        >
          <Table
            columns={couponSetColumns}
            dataSource={resellerDetail.couponSets || []}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: 'No coupon sets yet. Import CSV to add codes.' }}
            onRow={(record) => ({
              onClick: () => handleCouponSetClick(record.id),
              style: { cursor: 'pointer' }
            })}
          />
        </Card>

        <Card title="Recent Redemptions" className="redemptions-card">
          <RedemptionsTable
            resellerId={parseInt(id!)}
            recentRedemptions={resellerDetail.recentRedemptions || []}
            showPagination={false}
          />
        </Card>

        <CouponSetDetailModal
          couponSetId={selectedCouponSetId}
          visible={isCouponSetModalVisible}
          onClose={handleCloseCouponSetModal}
        />
      </div>
    </Layout>
  );
};

export default ResellerDetailPage;
